import { useEffect, useState } from "react";
import { demoApi, type CashFlowPlanner } from "@/api/client";
import { describeReadFailure } from "@/lib/apiFailure";

export type CashFlowPlannerStatus = "loading" | "ready" | "error";

export interface UseCashFlowPlannerResult {
  /** Null until the request resolves, or if it failed. */
  planner: CashFlowPlanner | null;
  status: CashFlowPlannerStatus;
  error: string | null;
}

/**
 * Fetches the tenant's real cash-flow projection from GET /api/demo/home/cash-flow —
 * ef-048-5. Deliberately a copy of useBalanceSheet's shape rather than a shared generic
 * (same convention that hook's own remarks state for useProfitAndLoss).
 *
 * A "ready" result with `planner.events` empty is a successful answer meaning nothing is
 * scheduled in this window — the caller renders the honest empty state
 * (CashFlowPlannerCard.tsx), never a spinner.
 */
export function useCashFlowPlanner(): UseCashFlowPlannerResult {
  const [planner, setPlanner] = useState<CashFlowPlanner | null>(null);
  const [status, setStatus] = useState<CashFlowPlannerStatus>("loading");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();

    async function load(): Promise<void> {
      setStatus("loading");
      setError(null);
      try {
        const result = await demoApi.getCashFlowPlanner(undefined, controller.signal);
        if (cancelled) return;
        setPlanner(result);
        setStatus("ready");
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === "AbortError")) return;
        setPlanner(null);
        setStatus("error");
        setError(describeReadFailure(err));
      }
    }

    void load();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return { planner, status, error };
}
